import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { unstable_batchedUpdates as batchUpdates } from 'react-dom';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { focusElement } from 'platform/utilities/ui';
import {
  VaButton,
  VaButtonSegmented,
  VaLoadingIndicator,
  VaPagination,
  VaSort,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import CombinedHistoryCard from './CombinedHistoryCard';
import { exportRows } from './CombinedHistoryFields';
import CombinedHistoryFilters from './CombinedHistoryFilters';
import CombinedHistoryFiltersModal from './CombinedHistoryFiltersModal';
import CombinedHistorySummary, {
  summarySentence,
} from './CombinedHistorySummary';
import CombinedHistoryTable from './CombinedHistoryTable';
import SubmissionFilesModal, {
  viewFilesFocusTarget,
} from './SubmissionFilesModal';
import { recordSubmissionHistoryLoad } from '../utilities/analytics';
import { submissionPropType } from '../utilities/combinedHistory';
import {
  EMPTY_MODAL_FILTERS,
  filterPhrases,
  filterSubmissions,
  formFiltersFor,
  searchWords,
} from '../utilities/combinedHistoryFilters';
import {
  historyParamsFor,
  paginate,
  readHistoryParams,
  sortColumnIndex,
  sortLabel,
  sortSubmissions,
} from '../utilities/combinedHistoryPaging';
import {
  COMBINED_HISTORY_COLUMNS,
  COMBINED_HISTORY_DIRECTIONS,
  COMBINED_HISTORY_SORT_KEYS,
} from '../utilities/constants';
import { downloadCsv } from '../utilities/csv';
import { useLaptopViewport } from '../utilities/useLaptopViewport';
import {
  HISTORY_VIEWS,
  readHistoryView,
  saveHistoryView,
  toHistoryView,
} from '../utilities/historyView';

/** @typedef {import('../utilities/combinedHistory').CombinedHistorySubmission} Submission */

/**
 * The view switch's buttons, in the order shown.
 *
 * @type {Array<{ label: string, value: string }>}
 */
const VIEW_BUTTONS = [
  { label: 'Table', value: HISTORY_VIEWS.TABLE },
  { label: 'Card', value: HISTORY_VIEWS.CARD },
];

const OPPOSITE_DIRECTION = Object.freeze({
  [COMBINED_HISTORY_DIRECTIONS.ASC]: COMBINED_HISTORY_DIRECTIONS.DESC,
  [COMBINED_HISTORY_DIRECTIONS.DESC]: COMBINED_HISTORY_DIRECTIONS.ASC,
});

/** The loader's own order, which the select writes to the URL as no sort. */
const NEWEST_SORT = `date:${COMBINED_HISTORY_DIRECTIONS.DESC}`;
const { ASC, DESC } = COMBINED_HISTORY_DIRECTIONS;

/**
 * The card view's sort select, one entry per direction of each table column,
 * worded as the portal's other sort selects word theirs. Newest comes before
 * Oldest and A to Z before Z to A, and a date sort descending set from the
 * table's header shows as Newest, since it is the same order.
 *
 * @type {readonly { value: string, label: string }[]}
 */
const CARD_SORT_OPTIONS = Object.freeze(
  COMBINED_HISTORY_COLUMNS.flatMap(column =>
    (column.key === 'date' ? [DESC, ASC] : [ASC, DESC]).map(dir => {
      const value = `${column.key}:${dir}`;
      return {
        value: value === NEWEST_SORT ? '' : value,
        label: column.sortOption[dir],
      };
    }),
  ),
);

/**
 * @param {string|null} sortKey
 * @param {string|null} direction
 * @returns {string} The select entry for the sort in force.
 */
const cardSortValue = (sortKey, direction) => {
  if (!sortKey) return '';
  const value = `${sortKey}:${direction}`;
  return value === NEWEST_SORT ? '' : value;
};

/**
 * @param {string} claimantName The claimant whose history the export holds,
 *   or empty for the full history.
 * @returns {string} The export's file name, with the claimant's name as
 *   letters and digits joined by hyphens and the time to the second, so
 *   exports for different claimants or a minute apart are told apart.
 * @example
 * exportFileName('Doe, John'); // 'submission-history-doe-john-2026-10-16-143012.csv'
 */
const exportFileName = claimantName => {
  const namePart = claimantName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const stamp = format(new Date(), 'yyyy-MM-dd-HHmmss');
  return `submission-history-${namePart ? `${namePart}-` : ''}${stamp}.csv`;
};

/**
 * Keeps the upload whose files are open, for the rows it was opened from.
 * New rows, from a Refresh or another claimant's page, close it, so the
 * modal never shows an upload the page no longer lists.
 *
 * @param {Submission[]} submissions The loader's rows.
 * @returns {[Submission|null, (submission: Submission) => void, () => void]}
 *   The open upload or null, and the handlers that open and close it.
 */
const useFilesShown = submissions => {
  const [shown, setShown] = useState(null);

  const openFiles = useCallback(
    submission => setShown({ submissions, submission }),
    [submissions],
  );
  const closeFiles = useCallback(() => setShown(null), []);

  return [
    shown?.submissions === submissions ? shown.submission : null,
    openFiles,
    closeFiles,
  ];
};

/**
 * Returns focus to an upload's View when its files close and focus has been
 * lost, as when the table swapped to cards while the modal was open and the
 * button va-modal remembered is gone. Focus anywhere else is left alone.
 *
 * @param {React.RefObject<HTMLElement>} rootRef The history's element.
 * @param {Submission|null} shown The open upload, or null.
 */
const useViewFocusOnClose = (rootRef, shown) => {
  const lastShownId = useRef(null);

  useEffect(() => {
    if (shown) {
      lastShownId.current = String(shown.id);
      return undefined;
    }
    const id = lastShownId.current;
    lastShownId.current = null;
    if (id === null) return undefined;

    // After va-modal has returned focus, if it could.
    const frame = requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== document.body) return;
      const view = Array.from(
        rootRef.current?.querySelectorAll('[data-submission-id]') ?? [],
      ).find(button => button.dataset.submissionId === id);
      if (view) viewFilesFocusTarget(view)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [rootRef, shown]);
};

/**
 * The combined submission history. A search box sits above a toolbar with
 * Refresh, Filters, Export, and the table-or-cards view switch. Below them
 * come the summary and either the rows, one page at a time, the loading
 * indicator, the error alert, or the empty message. The Filters dialog holds
 * the status, the submission type, the submitted date, and the confirmation
 * number, which apply together. There is no filter by claimant, since a list
 * of claimants' names is a privacy question and the search already finds a
 * claimant by name.
 *
 * The whole merged list is in the browser, so searching, filtering, sorting,
 * and paging happen here. The page, sort, and dialog filters live in the URL
 * (`readHistoryParams`), so a reload or a return lands on the same view. The
 * search text and the confirmation number do not, as either may be a name or
 * an identifier. The route runs no loader for a query change. A search,
 * filter, or sort change returns to the first page and keeps the controls up
 * when nothing matches. The cards and the export follow the table's sort.
 * After a search or filter change the table shows no sort arrow, though the
 * rows stay sorted and the summary says so, because handing the sort back to
 * va-table would move focus to the header.
 *
 * The view choice is saved in the browser and read as the state initializes,
 * so the first render already shows it. Below laptop width the cards are the only view and the saved choice waits
 * for a wider screen. The switch shows only with rows to switch between.
 *
 * On the first load the loading indicator takes focus and the toolbar waits
 * for the result, so nothing pushes down the summary, error headline, or
 * empty message that takes focus when the history settles, and Refresh's
 * loading state does not announce over it. A reload keeps the toolbar, the
 * summary, and the card sort in place and shows the indicator in place of
 * the rows and the pagination, so Refresh keeps its focus. Focus moves after
 * a reload only when the control the reader was on went with the result. A
 * search, filter, sort, or page change asked for during a reload applies to
 * the rows the reload brings. Filters and Export are disabled only while the
 * history reloads, as the design system asks that a button not be disabled
 * for any other reason. A partial history shows a warning above the rows it
 * has, and the warning stays through a reload so the summary's count never
 * shows without its caveat. Export saves every match, not only the page
 * shown, as CSV with the table's columns in the sorted order. View on an
 * upload, in either view, opens one modal with its files.
 *
 * @param {Object} props
 * @param {Submission[]} [props.submissions] The rows, in the loader's order.
 * @param {boolean} [props.hasError] Whether the history failed to load.
 * @param {boolean} [props.isLoading] Whether the history is loading.
 * @param {boolean} [props.partial] Whether either source left rows out of
 *   `submissions`.
 * @param {() => void} props.onRefresh Fetches the history again. The page
 *   passes the route's revalidate, so the URL stays as it is.
 * @param {string|null} [props.claimantId] The claimant the history is
 *   limited to. A change empties the search and the confirmation filter.
 *   Null for the full history.
 * @param {string} [props.claimantName] The claimant the history is limited
 *   to, for the export's file name. Empty for the full history.
 * @param {number} [props.loadStartedAt] When the loader that fetched the
 *   rows was entered, for the load-to-render report. Reported once per load.
 * @param {'representative'|'claimant'} [props.scope] Which history page,
 *   for the report.
 * @returns {JSX.Element}
 */
const CombinedHistory = ({
  submissions,
  hasError,
  isLoading,
  partial,
  onRefresh,
  claimantId,
  claimantName,
  loadStartedAt,
  scope,
}) => {
  const [view, setView] = useState(readHistoryView);
  const isLaptop = useLaptopViewport();
  const shownView = isLaptop ? view : HISTORY_VIEWS.CARD;
  const hasRows = !hasError && submissions.length > 0;

  const [searchParams, setSearchParams] = useSearchParams();
  const { page, sortKey, direction, status, form, date } = useMemo(
    () => readHistoryParams(searchParams),
    [searchParams],
  );
  const current = useMemo(
    () => ({ page, sortKey, direction, status, form, date }),
    [page, sortKey, direction, status, form, date],
  );
  // Kept out of the URL, as either may be a name or an identifier.
  const [query, setQuery] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  // Clear mounts the search box again, which empties it and drops a search
  // still waiting to apply.
  const [filtersKey, setFiltersKey] = useState(0);
  // A change asked for during a reload, applied once the reload ends. State
  // rather than a ref, so the summary can leave out a sort not yet in force.
  const [held, setHeld] = useState(null);
  // The dialog mounts again each time it opens, so its fields start from the
  // filters in force. va-modal returns focus to the Filters button on every
  // close, so no path here moves focus.
  const [modal, setModal] = useState({ open: false, count: 0 });
  // Another claimant's page keeps the component mounted. The reset runs in
  // render, so no frame shows their rows under the last claimant's search,
  // and without a key on the component, which would focus the indicator
  // again as on a first load.
  const [filtersClaimant, setFiltersClaimant] = useState(claimantId);
  if (filtersClaimant !== claimantId) {
    setFiltersClaimant(claimantId);
    setQuery('');
    setConfirmation(null);
    setFiltersKey(key => key + 1);
    setHeld(null);
    setModal(state => ({ ...state, open: false }));
  }
  const filters = useMemo(
    () => ({ query, status, form, date, confirmation }),
    [query, status, form, date, confirmation],
  );

  const openModal = () =>
    setModal(state => ({ open: true, count: state.count + 1 }));
  const closeModal = () => setModal(state => ({ ...state, open: false }));
  const filteredRows = useMemo(
    () => filterSubmissions(submissions, filters),
    [submissions, filters],
  );
  const sortedRows = useMemo(
    () => sortSubmissions(filteredRows, sortKey, direction),
    [filteredRows, sortKey, direction],
  );
  const paged = useMemo(() => paginate(sortedRows, page), [sortedRows, page]);
  const formOptions = useMemo(
    () => formFiltersFor(submissions, form),
    [submissions, form],
  );
  const phrases = useMemo(() => filterPhrases(filters), [filters]);
  const sortedBy = sortLabel({ sortKey, direction });
  // Shown on the Filters button, so the reader knows where the active
  // filters are.
  const dialogFilterCount = Object.keys(EMPTY_MODAL_FILTERS).filter(
    key => filters[key],
  ).length;
  const hasMatches = hasRows && filteredRows.length > 0;
  // va-table moves each row's cells out of its va-table-row, so React must
  // never update rows it has taken apart. Any change to the rows shown
  // remounts the table instead.
  const tableKey = [
    sortKey,
    direction,
    paged.page,
    searchWords(query).join(' '),
    status,
    form,
    date,
    confirmation,
  ].join('|');

  const rootRef = useRef(null);
  const [filesShown, openFiles, closeFiles] = useFilesShown(submissions);
  useViewFocusOnClose(rootRef, filesShown);
  const summaryRef = useRef(null);
  const errorRef = useRef(null);
  const emptyRef = useRef(null);
  // Only the first load's indicator takes focus.
  const [hasSettled, setHasSettled] = useState(!isLoading);
  const pendingCountRef = useRef(0);
  const handledCountRef = useRef(0);
  const [pending, setPending] = useState(null);
  const [restoreSort, setRestoreSort] = useState(null);
  // What the status region reads after a search or filter change, which
  // moves no focus. Empty after a change announced another way.
  const [announcement, setAnnouncement] = useState('');
  const announceTimerRef = useRef(null);
  // A second change in the same tick reads the URL here, since the render's
  // copy lags it.
  const searchRef = useRef(searchParams);
  searchRef.current = searchParams;
  const loadingRef = useRef(isLoading);
  loadingRef.current = isLoading;

  /**
   * Writes the history's URL parameters. A change the URL already has is
   * handled in place, with no navigation, so the loader does not run again.
   * During a reload the change is held, together with any search held with
   * it, and applied once the reload ends.
   *
   * @param {Partial<import('../utilities/combinedHistoryPaging').HistoryParams>} change
   *   The parameters that change. The rest keep the URL's values.
   * @param {{ focusSummary?: boolean, restore?: boolean, announce?: boolean }} [after]
   *   Whether to focus the summary once the change renders, hand the sort
   *   back to va-table, and read the summary's new sentence through the
   *   status region. The region is its own always-present element because a
   *   focused live region is read twice and an `aria-live` that switches on
   *   and off is not honoured everywhere.
   */
  const applyParams = useCallback(
    (
      change,
      { focusSummary = false, restore = false, announce = false } = {},
    ) => {
      if (loadingRef.current) {
        setHeld(state => ({
          query: state?.query,
          change: { ...state?.change, ...change },
          after: {
            focusSummary: focusSummary || Boolean(state?.after.focusSummary),
            restore: restore || Boolean(state?.after.restore),
            announce: announce || Boolean(state?.after.announce),
          },
        }));
        return;
      }
      const params = historyParamsFor(searchRef.current, {
        ...current,
        ...change,
      });
      pendingCountRef.current += 1;
      setPending({
        count: pendingCountRef.current,
        focusSummary,
        restore,
        announce,
        target: params.toString(),
      });
      // Navigating to the URL already shown would run the loaders again.
      if (params.toString() !== searchRef.current.toString()) {
        setSearchParams(params, { replace: true });
      }
    },
    [setSearchParams, current],
  );

  /**
   * Sorts the whole list and returns to the first page. va-table offers
   * ascending first whenever it holds no sort, so a request for the sort
   * already in force means the other direction, as a fresh click on a sorted
   * header gives.
   *
   * @param {{ columnIndex: number, direction: string }} request The sort
   *   va-table reports.
   */
  const requestSort = useCallback(
    ({ columnIndex, direction: requested }) => {
      const nextKey = COMBINED_HISTORY_SORT_KEYS[columnIndex];
      const nextDirection =
        nextKey === sortKey && requested === direction
          ? OPPOSITE_DIRECTION[requested]
          : requested;
      applyParams(
        { page: 1, sortKey: nextKey, direction: nextDirection },
        { restore: true },
      );
    },
    [applyParams, sortKey, direction],
  );

  /** @param {number} nextPage */
  const selectPage = nextPage =>
    applyParams({ page: nextPage }, { focusSummary: true });

  /**
   * Sorts from the card view's select. The summary's new sentence is read
   * out, since no header announces the sort here.
   *
   * @param {string} value The select's entry, `key:dir` or empty for newest
   *   first.
   */
  const selectCardSort = value => {
    const [nextKey = null, nextDirection = null] = value
      ? value.split(':')
      : [];
    applyParams(
      { page: 1, sortKey: nextKey, direction: nextDirection },
      { announce: true },
    );
  };

  /**
   * Applies a search, which keeps the sort and returns to the first page.
   * The updates are batched because React 17 does not batch a web
   * component's event, and a text change rendered alone would show the
   * narrowed rows against the old page for a frame and let the page
   * correction effect write the URL first.
   *
   * @param {{ query: string }} change The text to apply.
   */
  const changeQuery = useCallback(
    ({ query: nextQuery }) => {
      if (loadingRef.current) {
        setHeld(state => ({
          change: {},
          after: { focusSummary: false, restore: false, announce: false },
          ...state,
          query: nextQuery,
        }));
        return;
      }
      if (nextQuery.trim() === query.trim()) return;
      batchUpdates(() => {
        setQuery(nextQuery);
        applyParams({ page: 1 }, { announce: true });
      });
    },
    [applyParams, query],
  );

  /**
   * Applies the dialog's filters together and closes it. The confirmation
   * number stays in state and the rest go to the URL.
   *
   * @param {import('./CombinedHistoryFiltersModal').ModalFilters} filters
   */
  const applyModalFilters = ({ confirmation: nextConfirmation, ...next }) =>
    batchUpdates(() => {
      setConfirmation(nextConfirmation);
      applyParams({ page: 1, ...next }, { announce: true });
      closeModal();
    });

  /**
   * Clears the search and every filter. The Clear button unmounts with the
   * no-match state, so focus moves to the summary.
   */
  const clearFilters = () =>
    batchUpdates(() => {
      setQuery('');
      setConfirmation(null);
      setFiltersKey(key => key + 1);
      applyParams(
        { page: 1, status: null, form: null, date: null },
        { focusSummary: true },
      );
    });

  // Runs once a change the history asked for has rendered.
  useLayoutEffect(() => {
    if (!pending || pending.count === handledCountRef.current) return;
    if (searchParams.toString() !== pending.target) return;
    handledCountRef.current = pending.count;

    if (pending.restore && sortKey) {
      setRestoreSort({
        columnIndex: sortColumnIndex(sortKey),
        direction,
        token: pending.count,
      });
    }
    if (pending.focusSummary) focusElement(summaryRef.current);
    // The region is cleared and given the sentence a tick later, so the same
    // sentence twice in a row is still a change to announce.
    clearTimeout(announceTimerRef.current);
    setAnnouncement('');
    if (pending.announce) {
      const sentence = summarySentence({
        ...paged,
        filterPhrases: phrases,
        sortLabel: sortedBy,
      });
      announceTimerRef.current = setTimeout(() => setAnnouncement(sentence), 0);
    }
  }, [pending, searchParams, sortKey, direction, paged, phrases, sortedBy]);

  useEffect(() => () => clearTimeout(announceTimerRef.current), []);

  // The element in the history that last took focus. Chrome fires a focusout
  // with no related target when the focused element is removed, the same as
  // for a click on the page's background, so the element is kept and the
  // effect below checks whether it is still in the document. Safari gives a
  // clicked button no focus, so a mouse Refresh records nothing.
  const lastFocusedRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (root.contains(document.activeElement)) {
      lastFocusedRef.current = document.activeElement;
    }
    const entered = event => {
      lastFocusedRef.current = event.target;
    };
    const left = event => {
      if (event.relatedTarget && !root.contains(event.relatedTarget)) {
        lastFocusedRef.current = null;
      }
    };
    root.addEventListener('focusin', entered);
    root.addEventListener('focusout', left);
    return () => {
      root.removeEventListener('focusin', entered);
      root.removeEventListener('focusout', left);
    };
  }, []);

  // Removing the focused indicator drops focus to the body. Focus anywhere
  // else means the reader moved it during the load. After a reload, focus is
  // lost only when the control the reader was on unmounted with the result.
  useEffect(() => {
    if (isLoading) return;
    const active = document.activeElement;
    const lost = !active || active === document.body;
    const last = lastFocusedRef.current;
    if (!hasSettled) {
      setHasSettled(true);
      if (!lost) return;
    } else if (!lost || !last || last.isConnected) {
      return;
    }
    if (!hasError) {
      focusElement(hasRows ? summaryRef.current : emptyRef.current);
      return;
    }
    // va-alert stays hidden until it hydrates, and a hidden headline takes
    // no focus, so the focus waits for it. A reader who moved focus in the
    // meantime keeps it.
    const headline = errorRef.current;
    const alert = headline?.closest('va-alert');
    if (!alert?.componentOnReady) {
      focusElement(headline);
      return;
    }
    alert.componentOnReady().then(() => {
      const now = document.activeElement;
      if (headline.isConnected && (!now || now === document.body)) {
        focusElement(headline);
      }
    });
  }, [isLoading, hasSettled, hasError, hasRows]);

  // va-table gets the sort back once, on the first page, and only while it
  // is the sort in force. The table reports the restore so it is not
  // replayed when the table mounts again for Refresh or a view switch.
  const tableRestore =
    restoreSort &&
    paged.page === 1 &&
    restoreSort.columnIndex === sortColumnIndex(sortKey) &&
    restoreSort.direction === direction
      ? restoreSort
      : null;
  const clearRestore = useCallback(
    token =>
      setRestoreSort(restore => (restore?.token === token ? null : restore)),
    [],
  );

  // A page past the last one, as after a reload with fewer rows, reads as the
  // last page and the URL follows. Not while a change of this history's own
  // is still landing, since that change names its page and a second write
  // would navigate to a URL already shown, which runs the loaders again.
  useEffect(() => {
    if (pending && pending.count !== handledCountRef.current) return;
    if (hasRows && page !== paged.page) {
      setSearchParams(
        historyParamsFor(searchParams, { ...current, page: paged.page }),
        { replace: true },
      );
    }
  }, [
    hasRows,
    page,
    paged.page,
    pending,
    searchParams,
    setSearchParams,
    current,
  ]);

  // A held search that matches the text in force changes nothing, as when
  // it was typed and then cleared during the reload. A reload that ends in
  // an error or an empty history shows no summary, so nothing reads one.
  useEffect(() => {
    if (isLoading || !held) return;
    const searched =
      held.query !== undefined && held.query.trim() !== query.trim();
    const changed = searched || Object.keys(held.change).length > 0;
    batchUpdates(() => {
      setHeld(null);
      if (searched) setQuery(held.query);
      if (changed) {
        applyParams(searched ? { page: 1, ...held.change } : held.change, {
          ...held.after,
          announce: hasRows && (searched || held.after.announce),
        });
      }
    });
  }, [isLoading, held, query, hasRows, applyParams]);
  // The select shows a held sort at once. The summary names it only once it
  // is in force.
  const holdingSort = held?.change.sortKey !== undefined;

  // One report per load, when the loaded rows first render. The table waits
  // for va-table to build, and the cards render at once.
  const reportedRef = useRef(null);
  const rowCount = submissions.length;
  useEffect(() => {
    if (loadStartedAt == null || isLoading || hasError) return;
    if (reportedRef.current === loadStartedAt) return;
    reportedRef.current = loadStartedAt;

    (async () => {
      // The inner table exists only once va-table has loaded, so wait for
      // the host first, then for the table it builds.
      const table = rootRef.current?.querySelector('va-table');
      await table?.componentOnReady?.();
      await table?.querySelector('va-table-inner')?.componentOnReady?.();
      await Promise.resolve();
      recordSubmissionHistoryLoad({
        scope,
        view: shownView,
        rowCount,
        loadToRenderMs: performance.now() - loadStartedAt,
      });
    })();
  }, [loadStartedAt, isLoading, hasError, scope, shownView, rowCount]);

  /** @param {CustomEvent<{ label: string, value: string }>} event The va-button-segmented click. */
  const changeView = event => {
    const nextView = toHistoryView(event.detail.value);
    setView(nextView);
    saveHistoryView(nextView);
  };

  const exportHistory = () =>
    downloadCsv(exportFileName(claimantName), exportRows(sortedRows));

  // Boolean props are passed only when true, since a false value reaches a
  // web component as the string "false". Until the first rows arrive the
  // intro is the widest thing in the column, so the indicator centers under
  // it instead of under the table's width.
  const loadingIndicator = (
    <VaLoadingIndicator
      className={hasRows ? undefined : 'submissions__history-loading'}
      message="Loading submission history..."
      setFocus={!hasSettled || undefined}
    />
  );

  return (
    <div ref={rootRef}>
      {hasRows && (
        <CombinedHistoryFilters
          key={`filters-${filtersKey}`}
          query={query}
          onChange={changeQuery}
        />
      )}
      {hasRows && (
        <CombinedHistoryFiltersModal
          key={`dialog-${modal.count}`}
          visible={modal.open}
          filters={{ status, form, date, confirmation }}
          formOptions={formOptions}
          onApply={applyModalFilters}
          onClose={closeModal}
        />
      )}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="vads-u-visibility--screen-reader"
        data-testid="combined-history-announcement"
      >
        {announcement}
      </div>
      {(hasSettled || !isLoading) && (
        <div className="submissions__history-toolbar vads-u-display--flex vads-u-flex-wrap--wrap vads-u-align-items--flex-end vads-u-justify-content--space-between vads-u-margin-bottom--2">
          <div className="submissions__history-actions vads-u-display--flex vads-u-flex-wrap--wrap">
            <VaButton
              secondary
              text="Refresh"
              data-testid="combined-history-refresh"
              loading={isLoading || undefined}
              onClick={onRefresh}
            />
            {hasRows && (
              <VaButton
                secondary
                text={
                  dialogFilterCount > 0
                    ? `Filters (${dialogFilterCount})`
                    : 'Filters'
                }
                data-testid="combined-history-filters-toggle"
                disabled={isLoading || undefined}
                onClick={openModal}
              />
            )}
            {hasMatches && (
              <VaButton
                secondary
                text="Export"
                data-testid="combined-history-export"
                disabled={isLoading || undefined}
                onClick={exportHistory}
              />
            )}
          </div>
          {isLaptop && hasRows && (
            <VaButtonSegmented
              class="vads-u-margin-left--auto"
              data-testid="combined-history-view"
              label="View"
              buttons={VIEW_BUTTONS}
              selected={VIEW_BUTTONS.findIndex(button => button.value === view)}
              onVaButtonClick={changeView}
            />
          )}
        </div>
      )}
      {!isLoading && hasError && (
        <va-alert
          data-testid="combined-history-error"
          data-role="alert"
          status="error"
          visible
        >
          <h3 ref={errorRef} slot="headline">
            We couldn’t load the submission history
          </h3>
          <p className="vads-u-margin-y--0">
            Click Refresh or try again later.
          </p>
        </va-alert>
      )}
      {partial && !hasError && (hasRows || !isLoading) && (
        <va-alert
          class="vads-u-margin-bottom--2"
          data-testid="combined-history-partial"
          data-role="status"
          status="warning"
          visible
        >
          <h3 slot="headline">We couldn’t load all submissions</h3>
          <p className="vads-u-margin-y--0">
            This list may not show every submission. Refresh this page or try
            again later.
          </p>
        </va-alert>
      )}
      {!isLoading && !hasError && !hasRows && (
        <p ref={emptyRef} data-testid="combined-history-empty">
          No submissions found
        </p>
      )}
      {hasRows && (
        <CombinedHistorySummary
          ref={summaryRef}
          first={paged.first}
          last={paged.last}
          total={paged.total}
          filterPhrases={phrases}
          sortLabel={holdingSort ? null : sortedBy}
        />
      )}
      {!isLoading && hasRows && !hasMatches && (
        <div
          className="vads-u-margin-bottom--2"
          data-testid="combined-history-no-match"
        >
          <va-button
            secondary
            text="Clear search and filters"
            data-testid="combined-history-clear-filters"
            onClick={clearFilters}
          />
        </div>
      )}
      {hasMatches && shownView === HISTORY_VIEWS.CARD && (
        <VaSort
          class="vads-u-display--block vads-u-margin-bottom--2"
          data-testid="combined-history-card-sort"
          name="sort"
          value={
            holdingSort
              ? cardSortValue(held.change.sortKey, held.change.direction)
              : cardSortValue(sortKey, direction)
          }
          onVaSortSelect={event => selectCardSort(event.detail.value)}
        >
          {CARD_SORT_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </VaSort>
      )}
      {isLoading && loadingIndicator}
      {hasMatches && !isLoading && (
        <>
          {shownView === HISTORY_VIEWS.CARD && (
            <ul
              className="usa-unstyled-list"
              data-testid="combined-history-cards"
            >
              {paged.rows.map(submission => (
                <CombinedHistoryCard
                  key={submission.id}
                  submission={submission}
                  onViewFiles={openFiles}
                />
              ))}
            </ul>
          )}
          {shownView === HISTORY_VIEWS.TABLE && (
            <CombinedHistoryTable
              submissions={paged.rows}
              tableKey={tableKey}
              onSortRequest={requestSort}
              restoreSort={tableRestore}
              onRestored={clearRestore}
              onViewFiles={openFiles}
            />
          )}
          {paged.pages > 1 && (
            <VaPagination
              data-testid="combined-history-pagination"
              page={paged.page}
              pages={paged.pages}
              showLastPage
              onPageSelect={event => selectPage(event.detail.page)}
            />
          )}
        </>
      )}
      <SubmissionFilesModal submission={filesShown} onClose={closeFiles} />
    </div>
  );
};

CombinedHistory.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  claimantId: PropTypes.string,
  claimantName: PropTypes.string,
  hasError: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadStartedAt: PropTypes.number,
  partial: PropTypes.bool,
  scope: PropTypes.oneOf(['representative', 'claimant']),
  submissions: PropTypes.arrayOf(submissionPropType),
};

CombinedHistory.defaultProps = {
  claimantId: null,
  claimantName: '',
  hasError: false,
  isLoading: false,
  loadStartedAt: null,
  partial: false,
  scope: 'representative',
  submissions: [],
};

export default CombinedHistory;
